/**
 * 67.60, the vehicles to the D guns' bar (the owner, Oct 18: *"use the gun reference from d guns and use that to influence
 * the way the vehicle is improved"*; 65.36's D, rendering/gunLooks.ts): the bike, the technical, the supply truck, the 6×6
 * and the tracked carrier built as the D guns are — each part a side profile extruded across the body and chamfered where
 * the light catches (`slab`; a front or a top profile where the part faces that way), bare and utilitarian, a modest rise
 * in triangles — and in moving parts:
 *   - **wheels** in slabs (a treaded tyre ring, a pressed rim, a hub with its nuts; the bike's cast five-spoke wheels; the
 *     carrier's rubber-rimmed road wheels, a toothed sprocket and a spoked idler), turning, the fronts steering;
 *   - **tracks** of links with their guide horns (one instanced mesh a side, as today);
 *   - **doors** on hinges (the pickups' cab doors, the tailgate; the 6×6's roof hatches), **hatch lids** (the drivers',
 *     the carrier's turret hatches, the 6×6's cupola), the ramp as today, the bike's side stand — `OpenPart`s that
 *     `updateVehicleModel` turns from `VehicleMotion.doors` / `.hatches`;
 *   - the **guns** as today's (the gun lane's .50 on the cupola and the pintle; the carrier's tube on its cradle).
 * Same interface (`VehicleModel`) and the same anchors as today's builds — the crew's feet, their hands, the muzzle, the bay,
 * the bars, the bay's exits — and inside today's measured envelope, so the seats and the hit shapes (rendering/hitShapeFit.ts,
 * constants/hitboxes.ts) do not move (tests/vehicleLooks.test.ts holds both).
 *
 * The sheet: design_sheets/vehicles_d.html, today's beside each. Presentation only; not in play yet (sheet first).
 */
import type { MountArmament } from '../types/body';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { REFERENCE, SMOKE_LAUNCHER_FITS } from '../constants';
import type { UnitPalette } from './unitPalette';
import { buildPlayHmg, HMG_ON_CRADLE } from './gunLooks';
import { fitSmokeLaunchers } from './smokeLauncher';
import { buildMortarRig, poseMortar, MORTAR_PICKS } from './mortarLooks';   // 69.23: the mortar carrier's rig (the owner's picks)
import {
  vehicleShell, updateVehicleModel, mat, box, group, anchor, profile, loose, onSlope, arch, placeTrack, technicalBed, supplyLoad,
  BIKE, SADDLE_HIP_Y, TECHNICAL_MODEL, TRACKED_HULL, GLACIS, onGlacis, WHEELED_HULL, roofAt, HATCH_DEPTH, HMG_CUPOLA_STAND,
  type VehicleModel, type VehicleKind, type BikeVariant, type OpenPart, type TrackLoop,
} from './vehicleModels';

/** The kinds the D look builds (the walker keeps today's). */
export const D_VEHICLES = ['motorcycle', 'technical', 'supplyTruck', 'wheeled', 'tracked'] as const;
export type DVehicle = typeof D_VEHICLES[number];

const STEEL = '#5e615e', RUBBER = '#1d1e1f', GLASS = '#1c2328', LAMP = '#e8e2c8', RED = '#b8392c';

// ---------------------------------------------------------------------------
// Parts as the D guns build them
// ---------------------------------------------------------------------------

export type Pts = number[];
function shapeOf(pts: Pts, holes: Pts[]): THREE.Shape {
  const path = <T extends THREE.Path>(p: T, xy: Pts): T => { p.moveTo(xy[0], xy[1]); for (let i = 2; i < xy.length; i += 2) p.lineTo(xy[i], xy[i + 1]); return p; };
  const shape = path(new THREE.Shape(), pts);
  for (const h of holes) shape.holes.push(path(new THREE.Path(), h));
  return shape;
}
function extruded(pts: Pts, depth: number, c: number, holes: Pts[]): THREE.ExtrudeGeometry {
  const d = Math.max(1e-4, depth - 2 * c);
  const geo = new THREE.ExtrudeGeometry(shapeOf(pts, holes), c > 0
    ? { depth: d, bevelEnabled: true, bevelSegments: 1, bevelSize: c, bevelThickness: c, bevelOffset: -c, curveSegments: 1 }
    : { depth: d, bevelEnabled: false, curveSegments: 1 });
  geo.clearGroups();
  geo.translate(0, 0, -d / 2);
  return geo;
}
/**
 * A side profile (`pts`: z, y pairs, person units) extruded `w` across the body (x), centred on `x`; `c` chamfers its
 * edges inside the outline (the D guns' `slab`); `holes` cut through.
 */
export function slab(parent: THREE.Object3D, pts: Pts, w: number, color: string, x = 0, c = 0, holes: Pts[] = []): THREE.Mesh {
  const geo = extruded(pts, w, c, holes);
  geo.rotateY(-Math.PI / 2);   // the profile's x along the body (+z), its extrusion across it
  const m = new THREE.Mesh(geo, mat(color));
  m.position.x = x;
  parent.add(m);
  return m;
}
/** A front profile (`pts`: x, y pairs) extruded `d` along the body, centred on `z`. */
export function front(parent: THREE.Object3D, pts: Pts, d: number, color: string, z = 0, c = 0, holes: Pts[] = []): THREE.Mesh {
  const geo = extruded(pts, d, c, holes);
  const m = new THREE.Mesh(geo, mat(color));
  m.position.z = z;
  parent.add(m);
  return m;
}
/** A top profile (`pts`: x, z pairs) extruded `h` up, its base at `y`; `holes` cut through. */
export function top(parent: THREE.Object3D, pts: Pts, h: number, color: string, y = 0, c = 0, holes: Pts[] = []): THREE.Mesh {
  const flip = (q: Pts) => rev(q.map((v, i) => (i % 2 ? -v : v)));   // (z → −y flips the winding: turned back)
  const geo = extruded(flip(pts), h, c, holes.map(flip));
  geo.rotateX(-Math.PI / 2);   // the profile's y → −z (hence the flip), its extrusion → up
  geo.translate(0, h / 2, 0);
  const m = new THREE.Mesh(geo, mat(color));
  m.position.y = y;
  parent.add(m);
  return m;
}
/** A polygon round (cz, cy): `n` corners at `r` (a tread when `r2` is given: every other corner at `r2`), from `phase`. */
export function ring(cz: number, cy: number, r: number, n: number, phase = 0, r2 = r): Pts {
  const out: Pts = [];
  for (let i = 0; i < n; i++) { const a = phase + (2 * Math.PI * i) / n, q = i % 2 ? r2 : r; out.push(cz + q * Math.cos(a), cy + q * Math.sin(a)); }
  return out;
}
export const rev = (p: Pts): Pts => { const o: Pts = []; for (let i = p.length - 2; i >= 0; i -= 2) o.push(p[i], p[i + 1]); return o; };
/** A rectangle's profile from (a0, b0) to (a1, b1). */
export const rect = (a0: number, b0: number, a1: number, b1: number): Pts => [a0, b0, a1, b0, a1, b1, a0, b1];
export function cyl(parent: THREE.Object3D, r: number, len: number, color: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 8): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), mat(color));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
/** A hinged part: a group at its hinge (x, y, z), turned about `axis` by `angle` at full share, registered in the model's `opens`. */
function hinge(m: VehicleModel, parent: THREE.Object3D, x: number, y: number, z: number, axis: OpenPart['axis'], angle: number, kind: OpenPart['kind'], role?: OpenPart['role']): THREE.Group {
  const g = group(parent, x, y, z);
  (m.anim.opens ??= []).push({ group: g, axis, angle, base: 0, kind, ...(role ? { role } : {}) });
  return g;
}

/**
 * A wheel to the D bar, turning about x: `style` — `truck` a treaded tyre ring on a pressed rim with six nuts round the hub;
 * `bike` a narrow tyre on a cast five-spoke rim; `road` the carrier's rubber-rimmed disc with lightening holes; `sprocket`
 * a toothed drive wheel; `idler` a spoked idler. Returns the spinning group.
 */
function wheelD(parent: THREE.Object3D, r: number, w: number, p: UnitPalette, x: number, y: number, z: number, style: 'truck' | 'bike' | 'road' | 'sprocket' | 'idler'): THREE.Group {
  const g = group(parent, x, y, z);
  if (style === 'truck') {
    slab(g, ring(0, 0, r, 16, 0, r * 0.93), w, RUBBER, 0, 0.03 * r, [rev(ring(0, 0, r * 0.66, 8))]);    // the tyre: tread blocks round it
    slab(g, ring(0, 0, r * 0.68, 8), w * 0.72, STEEL, 0, 0, [rev(ring(0, 0, r * 0.3, 6))]);             // the rim, dished
    cyl(g, r * 0.3, w * 0.8, p.metal, 0, 0, 0, 'x', 6);                                                     // the hub
    for (let i = 0; i < 6; i++) { const a = (i * Math.PI) / 3; box(g, w * 0.84, r * 0.07, r * 0.07, p.dark, 0, Math.sin(a) * r * 0.2, Math.cos(a) * r * 0.2); }   // its nuts
  } else if (style === 'bike') {
    slab(g, ring(0, 0, r, 12), w, RUBBER, 0, 0.04 * r, [rev(ring(0, 0, r * 0.78, 12))]);
    const spokes: Pts[] = [];   // the rim's five openings between the spokes
    for (let i = 0; i < 5; i++) { const a0 = (i * 2 * Math.PI) / 5 + 0.22, a1 = a0 + (2 * Math.PI) / 5 - 0.44; spokes.push(rev([0.3 * r * Math.cos(a0), 0.3 * r * Math.sin(a0), 0.7 * r * Math.cos(a0), 0.7 * r * Math.sin(a0), 0.7 * r * Math.cos((a0 + a1) / 2), 0.7 * r * Math.sin((a0 + a1) / 2), 0.7 * r * Math.cos(a1), 0.7 * r * Math.sin(a1), 0.3 * r * Math.cos(a1), 0.3 * r * Math.sin(a1)])); }
    slab(g, ring(0, 0, r * 0.79, 10), w * 0.6, STEEL, 0, 0, spokes);
    cyl(g, r * 0.16, w * 1.1, p.metal, 0, 0, 0, 'x', 8);
  } else if (style === 'road') {
    slab(g, ring(0, 0, r, 10), w, RUBBER, 0, 0.04 * r, [rev(ring(0, 0, r * 0.8, 10))]);
    const holes = [0, 1, 2, 3].map(i => rev(ring(0.5 * r * Math.cos((i * Math.PI) / 2 + 0.4), 0.5 * r * Math.sin((i * Math.PI) / 2 + 0.4), r * 0.15, 4, Math.PI / 4)));
    slab(g, ring(0, 0, r * 0.81, 10), w * 0.86, STEEL, 0, 0, holes);
    cyl(g, r * 0.22, w * 1.05, p.metal, 0, 0, 0, 'x', 6);
  } else if (style === 'sprocket') {
    slab(g, ring(0, 0, r, 20, 0, r * 0.84), w * 0.5, STEEL, 0, 0, [rev(ring(0, 0, r * 0.3, 6))]);        // ten teeth
    slab(g, ring(0, 0, r * 0.62, 8), w, p.metal, 0, 0.03 * r);
    cyl(g, r * 0.26, w * 1.1, p.dark, 0, 0, 0, 'x', 6);
  } else {
    slab(g, ring(0, 0, r, 10), w, RUBBER, 0, 0.04 * r, [rev(ring(0, 0, r * 0.82, 10))]);
    const holes = [0, 2, 4].map(i => rev([0.32 * r * Math.cos(i * Math.PI / 3 + 0.2), 0.32 * r * Math.sin(i * Math.PI / 3 + 0.2), 0.72 * r * Math.cos(i * Math.PI / 3 + 0.2), 0.72 * r * Math.sin(i * Math.PI / 3 + 0.2), 0.72 * r * Math.cos(i * Math.PI / 3 + 0.85), 0.72 * r * Math.sin(i * Math.PI / 3 + 0.85), 0.32 * r * Math.cos(i * Math.PI / 3 + 0.85), 0.32 * r * Math.sin(i * Math.PI / 3 + 0.85)]));
    slab(g, ring(0, 0, r * 0.83, 10), w * 0.7, STEEL, 0, 0, holes);
    cyl(g, r * 0.2, w * 1.05, p.metal, 0, 0, 0, 'x', 6);
  }
  return g;
}

/** A track loop as today's, its links to the D bar: the pad and its guide horn (inward, between the road wheels), one instanced mesh. */
function trackLoopD(parent: THREE.Object3D, x: number, zr: number, zf: number, cy: number, r: number, width: number, p: UnitPalette): TrackLoop {
  const length = 2 * (zf - zr) + 2 * Math.PI * r, pitch = 0.25, count = Math.floor(length / pitch);
  const pad = new THREE.BoxGeometry(width, 0.08, pitch * 0.8);                              // (today's link: inside its loop)
  const horn = new THREE.BoxGeometry(0.08, 0.1, pitch * 0.45); horn.translate(0, -0.09, 0);  // the guide horn, inward (a link's +y faces out of its loop)
  for (const g of [pad, horn]) g.deleteAttribute('uv');
  const geo = mergeGeometries([pad, horn])!;
  const mesh = new THREE.InstancedMesh(geo, mat(p.dark), count);
  mesh.name = 'treads';
  mesh.frustumCulled = false;
  mesh.userData.noBake = true;
  parent.add(mesh);
  const loop: TrackLoop = { mesh, x, zr, zf, cy, r, length, pitch: length / count, offset: 0 };
  placeTrack(loop);
  return loop;
}

// ---------------------------------------------------------------------------
// The motorcycle
// ---------------------------------------------------------------------------

/**
 * The bike, D: a steel cradle frame in one profile with its openings, a teardrop tank and the saddle in chamfered slabs, a
 * finned cylinder (gas) or a ribbed pack (electric), cast five-spoke wheels, a raked fork with its stanchions and a
 * mudguard on the turning front, the bars, levers and mirrors; a side stand that is down while it stands. Today's
 * measures (`BIKE`): the rider's saddle, pegs and grips where they were.
 */
function motorcycleD(m: VehicleModel, p: UnitPalette, variant: BikeVariant): void {
  const { hull } = m, B = BIKE, R = B.wheelRadius, half = B.wheelbase / 2;
  m.anim.wheels.push(wheelD(hull, R, 0.13, p, 0, R, -half, 'bike'));
  m.anim.wheelRadius = R;
  // the swingarm: a tapered profile each side, from the pivot back to the axle; the shock from it up to the frame
  for (const side of [-1, 1]) slab(hull, [0.0, 0.47, -half, R + 0.03, -half, R - 0.04, 0.0, 0.39], 0.035, p.metal, side * 0.095, 0.006);
  cyl(hull, 0.03, 0.34, p.dark, 0.11, 0.6, -0.38, 'y', 6).rotation.x = -0.55;
  // the steering head, raked back; the fork, the front wheel, the mudguard and the bars turn about it (today's geometry)
  const head = group(hull, 0, 0.92, half - 0.2);
  head.rotation.x = -B.rake;
  const steer = group(head); m.anim.steer.push(steer);
  const fork = Math.hypot(0.92 - R, 0.2);
  for (const side of [-1, 1]) {
    cyl(steer, 0.022, fork * 0.6, STEEL, side * 0.085, -fork * 0.3, 0, 'y', 8);                       // the stanchion
    cyl(steer, 0.03, fork * 0.48, p.dark, side * 0.085, -fork * 0.76, 0, 'y', 8);                      // the lower leg over it
  }
  const frontWheel = group(steer, 0, -fork, 0);
  frontWheel.rotation.x = B.rake;
  m.anim.wheels.push(wheelD(frontWheel, R, 0.11, p, 0, 0, 0, 'bike'));
  const guard = group(steer, 0, -fork, 0); guard.rotation.x = B.rake;
  slab(guard, [-0.18, R + 0.02, -0.08, R + 0.07, 0.08, R + 0.07, 0.22, R + 0.01, 0.22, R - 0.02, 0.08, R + 0.04, -0.08, R + 0.04, -0.18, R - 0.01], 0.15, p.body, 0, 0.01);   // the mudguard, its arc over the tyre
  box(steer, 0.24, 0.05, 0.08, p.metal, 0, -0.02, 0);                                                    // the top clamp
  cyl(steer, 0.014, 0.7, p.metal, 0, 0.1, -0.05, 'x', 6);                                               // the bars
  for (const side of [-1, 1]) {
    cyl(steer, 0.022, 0.1, RUBBER, side * 0.31, 0.1, -0.05, 'x', 6);                                    // grips
    slab(group(steer, 0, 0.11, 0), [0.0, 0.0, -0.1, -0.01, -0.12, -0.03, 0.0, -0.02], 0.012, p.metal, side * 0.26);   // the lever
    box(steer, 0.012, 0.13, 0.012, p.metal, side * 0.24, 0.17, -0.04);                                  // the mirror's stalk
    slab(steer, ring(-0.04, 0.215, 0.04, 6), 0.012, p.dark, side * 0.25);                               //   and the mirror
  }
  cyl(steer, 0.07, 0.09, p.dark, 0, 0.0, 0.1, 'z', 10);                                                 // the headlamp's shell
  cyl(steer, 0.055, 0.012, LAMP, 0, 0.0, 0.15, 'z', 10);                                                //   and its lens
  m.bars = { left: anchor(steer, -B.gripX, 0.1, -0.05), right: anchor(steer, B.gripX, 0.1, -0.05) };
  // the cradle frame: one profile from the head round under the engine and back up to the seat rail, its openings cut
  slab(hull, [0.52, 0.98, 0.46, 0.8, 0.28, 0.4, -0.06, 0.36, -0.12, 0.5, -0.6, 0.8, -0.62, 0.86, -0.08, 0.86, 0.38, 0.98], 0.06, p.metal, 0, 0.01,
    [rev([0.38, 0.86, 0.3, 0.52, 0.04, 0.46, -0.02, 0.52, -0.02, 0.8, 0.28, 0.86]), rev([-0.12, 0.6, -0.46, 0.79, -0.12, 0.79])]);
  // the tank (a teardrop), its stripe; the saddle; the tail and its lamp; a side cover under the saddle
  slab(hull, [0.46, 0.9, 0.42, 1.0, 0.24, 1.06, 0.0, 1.02, -0.06, 0.92, 0.04, 0.84, 0.4, 0.84], 0.32, p.body, 0, 0.05);
  slab(hull, [0.4, 1.0, 0.24, 1.07, 0.0, 1.03, 0.0, 1.0, 0.24, 1.04, 0.38, 0.98], 0.07, p.flash, 0, 0.005);
  slab(hull, [0.0, B.saddleY - 0.06, 0.0, B.saddleY + 0.02, -0.12, B.saddleY + 0.04, -0.5, B.saddleY + 0.03, -0.58, B.saddleY - 0.02, -0.56, B.saddleY - 0.06], 0.27, RUBBER, 0, 0.03);
  slab(hull, [-0.5, 0.82, -0.88, 0.88, -0.9, 0.84, -0.86, 0.8, -0.5, 0.78], 0.2, p.body, 0, 0.02);
  box(hull, 0.13, 0.04, 0.02, RED, 0, 0.85, -0.89);
  slab(hull, [-0.02, 0.84, -0.42, 0.8, -0.4, 0.62, -0.1, 0.6], 0.24, p.dark, 0, 0.02);
  box(hull, 0.16, 0.02, 0.38, p.body, 0, R + 0.36, -half + 0.08).rotation.x = -0.25;                    // the rear mudguard
  for (const side of [-1, 1]) cyl(hull, 0.016, 0.1, p.metal, side * B.pegX, B.pegY, B.pegZ, 'x', 6);   // the pegs
  if (variant === 'gas') {
    // the engine: the crankcase, and the cylinder with its fins in one profile (a comb along its top); the exhaust's header
    // down and along the right side to the silencer, rising to the back
    slab(hull, [0.28, 0.36, 0.3, 0.52, -0.04, 0.52, -0.08, 0.38], 0.28, p.metal, 0, 0.03);
    const fins: Pts = [0.32, 0.52]; for (let i = 0; i < 5; i++) { const y = 0.56 + i * 0.045; fins.push(0.36, y, 0.36, y + 0.022, 0.32, y + 0.022, 0.32, y + 0.045); }
    fins.push(0.04, 0.785, 0.04, 0.52);
    slab(hull, fins, 0.3, STEEL, 0, 0.004);
    cyl(hull, 0.03, 0.34, p.metal, 0.12, 0.42, 0.24, 'y', 6).rotation.x = 0.9;                          // the header
    cyl(hull, 0.03, 0.62, p.metal, 0.17, 0.33, -0.12, 'z', 6).name = 'exhaust';
    cyl(hull, 0.055, 0.34, p.dark, 0.18, 0.46, -0.6, 'z', 8).rotation.x = Math.PI / 2 - 0.35;           // the silencer
  } else {
    // the pack: ribbed (a profile with notches), a charge strip along it, the hub motor's disc on the rear wheel
    const ribs: Pts = [0.33, 0.36]; for (let i = 0; i < 4; i++) { const z = 0.33 - i * 0.1; ribs.push(z, 0.66, z - 0.03, 0.66, z - 0.03, 0.64, z - 0.07, 0.64, z - 0.07, 0.66); }
    ribs.push(-0.1, 0.66, -0.1, 0.36);
    slab(hull, ribs, 0.3, p.dark, 0, 0.02).name = 'battery';
    box(hull, 0.31, 0.03, 0.2, p.flash, 0, 0.56, 0.12);
    cyl(hull, 0.16, 0.17, p.metal, 0, R, -half, 'x', 12);
  }
  // the side stand on its pivot (left), down while the bike stands still, folded up along the frame when it moves
  const stand = hinge(m, hull, -0.11, 0.38, -0.06, 'x', 1.25, 'stand');
  stand.rotation.x = 0;
  slab(stand, [0.02, 0.0, -0.02, 0.0, -0.05, -0.37, -0.0, -0.38], 0.03, p.metal);
  (m.anim.opens!.at(-1) as OpenPart).base = -1.25;   // folded back along the frame; down at full share
  m.crew.driver = anchor(hull, 0, B.saddleY - SADDLE_HIP_Y, B.saddleZ);
  // far tier: today's
  box(m.silhouette, 0.36, 0.5, 1.5, p.body, 0, 0.72, 0);
  box(m.silhouette, 0.14, 2 * R, 2 * R, p.dark, 0, R, -half);
  box(m.silhouette, 0.14, 2 * R, 2 * R, p.dark, 0, R, half);
}

// ---------------------------------------------------------------------------
// The pickup (the technical and the supply truck)
// ---------------------------------------------------------------------------

/**
 * The pickup, D: the cab a chamfered shell with its doors on hinges (each a slab with its window cut, the dark glass behind
 * it), the hood with its seam and the grille a slotted plate, the bush bar one frame with its openings, the bed's walls
 * with their stake pockets, the tailgate on its hinge, treaded wheels on pressed rims. Today's measures (TECHNICAL_MODEL):
 * the cab, the bed and the crew's seats where they were.
 */
function pickupD(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m, T = TECHNICAL_MODEL;
  for (const side of [-1, 1]) for (const z of [1.6, -1.3]) {
    const steer = group(full, side * 0.8, T.wheelR, z);
    m.anim.wheels.push(wheelD(steer, T.wheelR, 0.28, p, 0, 0, 0, 'truck'));
    if (z > 0) m.anim.steer.push(steer);
  }
  m.anim.wheelRadius = T.wheelR;
  const fit = group(full); m.anim.fittings = fit;
  for (const x of [-0.42, 0.42]) box(hull, 0.12, 0.16, T.front - T.back - 0.3, p.dark, x, 0.46, (T.front + T.back) / 2);   // the chassis rails
  // the hood (today's profile), its seam each side and across; each wheel in its well under a flared arch
  profile(hull, [[T.screenBase, T.sill], [T.front, T.sill], [T.front, 0.98], [T.front - 0.15, T.hoodFront], [T.screenBase, T.hoodTop]], 2 * T.half, p.body, 0, 0.05).name = 'hood';
  for (const side of [-1, 1]) box(hull, 0.012, 0.012, T.front - T.screenBase - 0.25, p.dark, side * 0.62, T.hoodTop - 0.03, (T.front + T.screenBase) / 2);
  for (const side of [-1, 1]) for (const z of [1.6, -1.3]) {
    profile(hull, arch(z, T.wheelR, 0, 0.5).slice(0, 10), 0.14, p.dark, side * (T.half - 0.04));   // (its face 3 cm proud of the hood's and the bed's sides: 1 cm fought them)
    profile(fit, arch(z, T.wheelR, 0.48, 0.56), 0.24, p.body, side * (T.half + 0.04)).name = `flare ${side} ${z}`;
  }
  // the grille: a slotted plate (its slots cut), the team's band over it, round lamps in their bezels
  const slots: Pts[] = []; for (let i = -3; i <= 3; i++) slots.push(rev(rect(i * 0.16 - 0.05, 0.68, i * 0.16 + 0.05, 0.92)));
  front(hull, rect(-0.62, 0.62, 0.62, 0.98), 0.04, p.metal, T.front + 0.015, 0.01, slots);
  box(hull, 1.18, 0.3, 0.02, RUBBER, 0, 0.8, T.front + 0.012);                                        // dark behind the slots (in front of the hood's face, inside the plate: it sat on the face and fought it)
  box(hull, 1.3, 0.05, 0.04, p.flash, 0, 0.99, T.front + 0.01);
  for (const x of [-0.7, 0.7]) { cyl(hull, 0.085, 0.05, p.metal, x, 0.84, T.front + 0.02, 'z', 10); cyl(hull, 0.068, 0.02, LAMP, x, 0.84, T.front + 0.045, 'z', 10); }
  // the bush bar: one frame, its openings cut (loose: a damaged form drops it)
  const bar = loose(group(fit));
  front(bar, [-0.85, 0.56, 0.85, 0.56, 0.85, 1.08, -0.85, 1.08], 0.06, p.metal, T.front + 0.16, 0.012,
    [rev(rect(-0.79, 0.62, -0.36, 1.0)), rev(rect(-0.24, 0.62, 0.24, 1.0)), rev(rect(0.36, 0.62, 0.79, 1.0))]);
  // the cab: a shell a door's thickness narrower each side (named: the hit shape measures it), the windscreen and the back
  // window dark glass; the side glass behind each door's window
  const cab = group(hull); cab.name = 'cab';
  const shellHalf = T.half - 0.045;
  profile(cab, [[T.cabBack, T.sill], [T.screenBase, T.sill], [T.screenBase, T.hoodTop], [T.roofFront, T.cabRoof], [T.cabBack, T.cabRoof]], 2 * shellHalf, p.body, 0, 0.05);
  const rake = Math.atan2(T.screenBase - T.roofFront, T.cabRoof - T.hoodTop);
  const screen = box(cab, 1.5, Math.hypot(T.screenBase - T.roofFront, T.cabRoof - T.hoodTop) - 0.12, 0.03, GLASS, 0, (T.cabRoof + T.hoodTop) / 2, (T.roofFront + T.screenBase) / 2 + 0.02);
  screen.rotation.x = -rake;
  box(cab, 1.5, 0.34, 0.03, GLASS, 0, 1.6, T.cabBack - 0.01);
  for (const side of [-1, 1]) {
    slab(cab, [0.06, 1.34, 1.02, 1.34, 0.86, 1.76, 0.06, 1.76], 0.01, GLASS, side * (shellHalf + 0.025));   // (mid-door: 1 mm off its inside and 11 mm off the cab fought them)
    // the door: hinged at its front edge, swinging out (a slab with its window cut; its handle; its seam is its edge)
    const door = hinge(m, cab, side * (T.half - 0.022), 0, T.screenBase - 0.06, 'y', -side * 1.15, 'door');
    slab(door, [-1.2, T.sill + 0.03, 0.0, T.sill + 0.03, 0.0, T.hoodTop + 0.02, -0.38, T.cabRoof - 0.06, -1.2, T.cabRoof - 0.06], 0.045, p.body, 0, 0.012,
      [rev([-1.12, 1.34, -0.16, 1.34, -0.34, 1.74, -1.12, 1.74])]);
    box(door, 0.02, 0.03, 0.14, p.metal, side * 0.03, 1.12, -0.95);
    const mirror = loose(group(fit, side * (T.half + 0.1), 1.42, 0.72));
    box(mirror, 0.14, 0.03, 0.03, p.metal, -side * 0.06, 0, 0);
    slab(mirror, [-0.05, -0.06, 0.05, -0.06, 0.05, 0.1, -0.05, 0.1], 0.05, p.dark, side * 0.02, 0.01);
  }
  m.crew.driver = anchor(cab, 0.45, 0.75, 0.55);   // (seated in the cab: drawn hidden; the seat's place)
  m.crew.passenger = anchor(cab, -0.45, 0.75, 0.55);
  m.hiddenCrew = ['driver', 'passenger'];
  // the bed: its floor, the walls (each a profile, its top edge chamfered, the rail capped) with their stake pockets
  const bedLen = T.cabBack - 0.05 - T.back;
  box(hull, 2 * T.half, 0.08, bedLen, p.body, 0, T.bedFloor, T.back + bedLen / 2);
  for (const side of [-1, 1]) {
    slab(hull, rect(T.back + 0.06, T.bedFloor, T.cabBack - 0.05, T.rail), 0.05, p.body, side * (T.half - 0.025), 0.012);
    box(hull, 0.08, 0.04, bedLen, p.metal, side * (T.half - 0.025), T.rail + 0.02, T.back + bedLen / 2);
    for (let i = 0; i < 4; i++) box(hull, 0.09, 0.08, 0.08, p.dark, side * (T.half - 0.025), T.rail - 0.04, T.back + 0.35 + i * (bedLen - 0.7) / 3);   // (2 cm proud of the wall each side)
    loose(box(hull, 0.18, 0.1, 0.04, RED, side * (T.half - 0.16), 1.12, T.back - 0.02));
  }
  // the tailgate on its hinge at the bed's floor, falling open behind; its latch line and its two catches
  const gate = hinge(m, hull, 0, T.sill + 0.04, T.back + 0.03, 'x', -Math.PI / 2, 'tailgate');
  front(gate, rect(-T.half, 0, T.half, T.rail - T.sill - 0.04), 0.06, p.body, 0, 0.012);
  box(gate, 1.5, 0.05, 0.02, p.dark, 0, T.rail - T.sill - 0.32, -0.035);
  for (const x of [-0.72, 0.72]) box(gate, 0.06, 0.06, 0.03, p.metal, x, T.rail - T.sill - 0.1, -0.04);
  box(hull, 2 * T.half - 0.1, 0.12, 0.14, p.metal, 0, 0.5, T.back - 0.06);                             // the step bumper
}

function technicalD(m: VehicleModel, p: UnitPalette, armament?: MountArmament): void {
  pickupD(m, p);
  technicalBed(m, p, m.hull, m.anim.fittings!, TECHNICAL_MODEL, armament);   // the roll bar, spare, cans, aerial, the .50 on its pintle (the gun lane's), its far tier
}
function supplyTruckD(m: VehicleModel, p: UnitPalette): void {
  pickupD(m, p);
  supplyLoad(m, p);   // today's load: the crates, the lashed tarp, the straps, the far tier's hump
}

// ---------------------------------------------------------------------------
// The 6×6
// ---------------------------------------------------------------------------

/**
 * The 6×6, D: today's chamfered hull, with its side armour in plates (chamfered, inside the flares), the driver's lid on
 * its hinge (the cupola has none, as today: a lid there would stand outside the turret's measured box however it opened), the bay's two roof hatches on theirs, the headlamps in their guards, the ramp a chamfered plate
 * with its ribs and vision slot, treaded wheels on pressed rims (the front pair steering). The .50 on the cupola is the gun
 * lane's (today's mount, its measured stand).
 */
function wheeledD(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m;
  for (const side of [-1, 1]) for (const z of [2.15, 0.6, -1.55]) {
    const steer = group(full, side * 1.2, 0.56, z);
    m.anim.wheels.push(wheelD(steer, 0.56, 0.44, p, 0, 0, 0, 'truck'));
    if (z > 2) m.anim.steer.push(steer);
  }
  m.anim.wheelRadius = 0.56;
  profile(hull, WHEELED_HULL, 2.3, p.body, 0, 0.2).name = 'body';
  const fit = group(full); m.anim.fittings = fit;
  for (const side of [-1, 1]) {
    // the side armour: a plate between each pair of arches and one over the bay, chamfered, a hand's width proud of the hull
    for (const [z0, z1] of [[-3.15, -2.3], [-0.85, -0.05], [1.25, 1.55]] as const) slab(hull, rect(z0, 1.12, z1, 1.98), 0.04, p.body, side * 1.17, 0.015);
    box(hull, 0.06, 0.32, 5.4, p.flash, side * 1.2, 1.62, -0.3);                                     // the flash stripe, 4 cm over the plates (closer, the depth buffer fights)
    for (const z of [2.15, 0.6, -1.55]) {
      profile(hull, arch(z, 0.56, 0, 0.64).slice(0, 10), 0.17, p.dark, side * 1.155);
      profile(fit, arch(z, 0.56, 0.62, 0.7), 0.42, p.body, side * 1.22).name = `flare ${side} ${z}`;
    }
    for (const z of [1.0, -0.75, -1.65, -2.55]) slab(hull, rect(z - 0.2, 1.81, z + 0.2, 1.99), 0.05, GLASS, side * 1.16, 0.01);   // vision blocks
    loose(slab(fit, rect(-0.77, 0.88, -0.17, 1.28), 0.25, p.dark, side * 1.275, 0.03));             // stowage between the back wheels
    loose(box(hull, 0.16, 0.12, 0.05, RED, side * 0.92, 1.95, -3.3));
  }
  for (const [y, k] of [[0.95, 0], [1.35, 1]] as const) box(fit, 0.2, 0.04, REFERENCE.step.tread, p.metal, -1.25, y, -2.75 - k * 0.02);
  box(hull, 0.04, 0.04, 0.45, p.metal, -1.08, 2.12, -2.75);
  box(hull, 1.8, 0.42, 0.06, GLASS, 0, 1.78, 2.62).rotation.x = -0.75;                                  // the windscreen band
  for (const x of [-0.85, 0.85]) {
    // a headlamp in its bezel under a guard; tow hooks
    cyl(hull, 0.11, 0.06, p.metal, x, 1.12, 3.3, 'z', 10); cyl(hull, 0.085, 0.02, LAMP, x, 1.12, 3.335, 'z', 10);
    loose(front(hull, [x - 0.17, 1.0, x + 0.17, 1.0, x + 0.17, 1.26, x - 0.17, 1.26], 0.05, p.metal, 3.28, 0.01, [rev(rect(x - 0.13, 1.04, x + 0.13, 1.22))]));
    box(hull, 0.1, 0.12, 0.12, p.metal, x * 0.82, 0.66, 3.14);
  }
  // the driver's hatch: its rim, the lid on its rear hinge, laid open back on the roof (today's look) and shut over it
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, -0.55, 2.29, 1.25);
  const lid = hinge(m, hull, -0.55, roofAt(0.95) + 0.04, 0.95, 'x', -Math.PI, 'hatch', 'driver');
  front(lid, rect(-0.29, 0, 0.29, 0.04), 0.58, p.metal, 0.29, 0.01).name = 'lid';
  m.crew.driver = anchor(hull, -0.55, 2.29 - HATCH_DEPTH, 1.25);
  // the bay's roof hatches, on their rear hinges (shut; they open with the doors)
  for (const x of [-0.45, 0.45]) {
    const h = hinge(m, hull, x, roofAt(-2.69), -2.69, 'x', -1.2, 'bayHatch');
    front(h, rect(-0.3, 0, 0.3, 0.035), 0.6, p.body, 0.3, 0.008);
    box(h, 0.08, 0.02, 0.1, p.metal, 0, 0.035, 0.5);
  }
  // the cupola: a ring with its rim, the gunner standing in it, its lid hinged behind him; the .50 on the cradle
  const turret = group(hull, 0.35, 2.27, -0.3); m.turret = turret;
  top(turret, ring(0, 0, 0.45, 8, Math.PI / 8), 0.3, p.dark, 0, 0.03);
  top(turret, ring(0, 0, 0.47, 8, Math.PI / 8), 0.04, p.metal, 0.28);
  m.crew.gunner = anchor(turret, 0, 0.3 - HATCH_DEPTH, 0);
  const cradle = group(turret, 0.0, 0.42, 0.3); m.cradle = cradle;
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);   // (the gun lane's .50, as play draws it: 65.36 D)
  m.crew.gunner.position.y += HMG_CUPOLA_STAND;
  m.muzzle = hmg.muzzle;
  m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  // the ramp, hinged at its foot: a chamfered plate, its vision slot, the band and the ribs inside (its tread, down)
  const ramp = group(hull, 0, 0.6, -3.28); m.anim.ramp = ramp;
  front(ramp, rect(-0.7, 0, 0.7, 1.55), 0.09, p.body, 0, 0.02, [rev(rect(-0.2, 1.22, 0.2, 1.3))]);
  box(ramp, 0.4, 0.08, 0.02, GLASS, 0, 1.26, 0.02);
  box(ramp, 1.42, 0.08, 0.1, p.flash, 0, 1.5, 0);
  for (const y of [0.25, 0.55, 0.85, 1.15]) box(ramp, 1.24, 0.04, 0.03, p.metal, 0, y, 0.06);
  box(hull, 1.5, 1.6, 0.04, p.dark, 0, 1.4, -3.27);
  box(hull, 1.3, 0.04, 0.03, p.metal, 0, 0.66, -3.3);
  for (const x of [-0.6, 0.6]) box(hull, 0.2, 0.5, 0.02, p.body, x, 0.98, -3.3);
  m.bay = anchor(ramp, 0, 1.6, 0);
  for (const x of [-1.35, -0.45, 0.45, 1.35]) m.bayExits.push(anchor(full, x, 0, -5.4 - Math.abs(x) * 0.3));
  // far tier: today's
  profile(m.silhouette, WHEELED_HULL, 2.3, p.body);
  for (const z of [2.15, 0.6, -1.55]) box(m.silhouette, 2.86, 1.08, 1.0, p.dark, 0, 0.54, z);
  const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.46, 0.3, 8), mat(p.dark)); sc.position.set(0.35, 2.42, -0.3); m.silhouette.add(sc);
}

// ---------------------------------------------------------------------------
// The tracked carrier
// ---------------------------------------------------------------------------

/**
 * The carrier, D: today's chamfered hull and glacis; the running gear to the bar (links with grousers and horns, rubber-
 * rimmed road wheels, a toothed sprocket, a spoked idler); the fenders and skirts chamfered plates; the turret a chamfered
 * top profile (its faces sloped inside today's hexagon) with its hatches' lids on hinges and the driver's lid swinging
 * over on its side hinge; the tube, the mantlet and the pintle gun as today's.
 */
function trackedD(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m;
  for (const side of [-1, 1]) {
    const x = side * 1.22;
    m.anim.tracks.push(trackLoopD(full, x, -2.35, 2.25, 0.46, 0.46, 0.56, p));
    m.anim.wheels.push(wheelD(full, 0.42, 0.42, p, x, 0.46, -2.35, 'sprocket'));
    m.anim.wheels.push(wheelD(full, 0.38, 0.42, p, x, 0.46, 2.25, 'idler'));
    for (let i = 0; i < 5; i++) m.anim.wheels.push(wheelD(full, 0.33, 0.4, p, x, 0.37, -1.6 + i * 0.8, 'road'));
  }
  m.anim.wheelRadius = 0.36;
  profile(hull, TRACKED_HULL, 2.0, p.body, 0, 0.12).name = 'body';
  for (const side of [-1, 1]) {
    slab(hull, [-2.65, 0.94, 2.5, 0.94, 2.62, 1.0, 2.5, 1.02, -2.65, 1.02], 0.62, p.body, side * 1.22, 0.015);   // the fender, its nose turned down
    box(hull, 0.64, 0.1, 0.2, p.flash, side * 1.22, 0.99, 2.48);
    for (const z of [-1.55, 0, 1.55]) loose(slab(hull, rect(z - 0.74, 0.68, z + 0.74, 0.94), 0.03, p.body, side * 1.515, 0.01));   // the skirts
    for (const z of [-1.6, -2.25]) loose(slab(hull, rect(z - 0.3, 1.02, z + 0.3, 1.34), 0.4, p.dark, side * 1.25, 0.03));       // stowage bins
    const [lz, ly] = onGlacis(0.62);
    loose(onSlope(hull, 0.2, 0.08, 0.14, LAMP, side * 0.7, ly, lz, GLACIS));
    loose(onSlope(hull, 0.26, 0.1, 0.03, p.metal, side * 0.7, ly, lz - 0.09, GLACIS));
    box(hull, 0.1, 0.14, 0.1, p.metal, side * 0.6, 0.62, 2.72);
    loose(box(hull, 0.16, 0.1, 0.03, RED, side * 0.78, 1.25, -2.745));
    slab(hull, rect(-1.91, 1.42, -1.65, 1.47), 0.34, p.dark, side * 0.55, 0.01);                     // exhaust louvres
  }
  { const [z, y] = onGlacis(0.32); for (const x of [-0.66, -0.33, 0, 0.33, 0.66]) loose(onSlope(hull, 0.28, 0.05, 0.22, p.dark, x, y, z, GLACIS)); }
  const grille: Pts[] = []; for (let i = 0; i < 6; i++) grille.push(rev(rect(-0.75 + i * 0.26, -2.6, -0.6 + i * 0.26, -2.1)));
  box(hull, 1.6, 0.02, 0.5, RUBBER, 0, 1.425, -2.35);                                                   // dark under the slats
  top(hull, rect(-0.85, -2.65, 0.85, -2.05), 0.05, p.metal, 1.43, 0.008, grille);                       // the engine deck's grille
  // the driver's hatch: its rim, the lid on its inboard hinge, swung over flat on the deck (today's look) and shut over it
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, 0.48, 1.45, 1.05);
  const dl = hinge(m, hull, 0.18, 1.475, 1.05, 'z', Math.PI, 'hatch', 'driver');
  top(dl, rect(0, -0.29, 0.58, 0.29), 0.05, p.metal, 0, 0.012).name = 'lid';
  box(hull, 0.3, 0.05, 0.08, p.dark, 0.48, 1.445, 1.44);
  m.crew.driver = anchor(hull, 0.48, 1.45 - HATCH_DEPTH, 1.05);
  // the turret: a chamfered top profile, its band; the spotter's cupola with vision blocks; two lids on hinges behind
  const turret = group(hull, 0, 1.42, -0.35); m.turret = turret;
  top(turret, [0, 1.08, 0.52, 0.92, 0.94, 0.34, 0.94, -0.58, 0.55, -1.0, -0.55, -1.0, -0.94, -0.58, -0.94, 0.34, -0.52, 0.92], 0.55, p.body, 0.005, 0.1).name = 'body';
  top(turret, [0, 1.1, 0.53, 0.94, 0.955, 0.34, 0.955, -0.59, 0.56, -1.01, -0.56, -1.01, -0.955, -0.59, -0.955, 0.34, -0.53, 0.94], 0.09, p.flash, 0.02);
  cyl(turret, 0.34, 0.24, p.dark, -0.45, 0.66, -0.35, 'y', 10);
  for (let k = 0; k < 5; k++) { const a = -1.2 + k * 0.6; box(turret, 0.12, 0.07, 0.05, GLASS, -0.45 + Math.sin(a) * 0.35, 0.72, -0.35 + Math.cos(a) * 0.35).rotation.y = a; }
  for (const [hx, hz, hy] of [[-0.45, -0.71, 0.78], [0.45, -0.76, 0.58]] as const) {
    // shut, the lid lies over its hatch; open (full share) it stands behind its occupant as today's
    const lidG = hinge(m, turret, hx, hy, hz, 'x', -(Math.PI / 2 - 0.32), 'hatch', hx < 0 ? 'spotter' : 'gunner');
    front(lidG, rect(-0.3, 0, 0.3, 0.05), 0.56, p.body, 0.28, 0.012).name = 'lid';
  }
  m.crew.spotter = anchor(turret, -0.45, 0.78 - HATCH_DEPTH, -0.35);
  box(turret, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, 0.45, 0.58, -0.45);
  m.crew.gunner = anchor(turret, 0.45, 0.58 - HATCH_DEPTH, -0.45);
  box(turret, 0.06, 0.24, 0.06, p.metal, 0.45, 0.58 + 0.12, -0.12);
  const pintle = group(turret, 0.45, 0.58 + 0.27, -0.12);
  slab(pintle, [-0.17, -0.05, 0.25, -0.05, 0.25, 0.055, -0.17, 0.055], 0.1, p.metal, 0, 0.012);        // receiver
  cyl(pintle, 0.02, 0.5, p.metal, 0, 0.01, 0.48, 'z', 6);                                                // barrel
  slab(pintle, rect(-0.01, -0.07, 0.13, 0.03), 0.12, p.dark, -0.1, 0.01);                               // the ammunition box
  m.hands.gunner = { right: anchor(pintle, 0.07, -0.02, -0.24), left: anchor(pintle, -0.07, -0.02, -0.24) };
  for (const side of [-1, 1]) {
    const fr = group(turret, side * 0.45, 0.36, 0.78); fr.rotation.y = side * Math.PI / 6;
    for (const t of [-0.15, 0, 0.15]) { const c = cyl(fr, 0.05, 0.22, p.dark, t, 0, 0.04, 'y', 6); c.rotation.x = Math.PI / 2 - 0.55; loose(c); }
    const rear = group(turret, side * 0.485, 0.3, -0.84); rear.rotation.y = -side * Math.PI / 6;
    loose(slab(rear, rect(-0.11, -0.14, 0.11, 0.14), 0.55, p.dark, 0, 0.03));
  }
  box(turret, 0.1, 0.08, 0.1, p.metal, 0, 0.6, -0.8);
  const aerial = loose(box(turret, 0.025, 1.3, 0.025, p.metal, 0, 1.25, -0.8)); aerial.rotation.x = -0.12; aerial.name = 'aerial';
  const cradle = group(turret, 0, 0.62, 0.62); m.cradle = cradle;
  box(cradle, 0.5, 0.42, 0.5, p.metal, 0, 0, 0);
  front(cradle, [-0.31, -0.23, 0.31, -0.23, 0.31, 0.17, 0.2, 0.23, -0.2, 0.23, -0.31, 0.17], 0.2, p.body, 0.35, 0.03);   // the mantlet, its top corners cut
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.18, 2.1, 12), mat(p.metal)); tube.rotation.x = Math.PI / 2; tube.position.z = 1.0; cradle.add(tube);
  cyl(cradle, 0.21, 0.3, p.metal, 0, 0, 1.15, 'z', 12);
  cyl(cradle, 0.2, 0.1, p.flash, 0, 0, 1.62, 'z', 12);
  front(cradle, [-0.15, -0.12, 0.15, -0.12, 0.15, 0.12, -0.15, 0.12], 0.26, p.metal, 1.91, 0.02, [rev(rect(-0.05, -0.05, 0.05, 0.05))]);   // the muzzle brake, its bore open
  m.muzzle = anchor(cradle, 0, 0, 2.06);
  // far tier: today's
  box(m.silhouette, 3.05, 0.95, 5.3, p.dark, 0, 0.5, -0.05);
  profile(m.silhouette, TRACKED_HULL, 2.0, p.body);
  const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.1, 0.6, 6), mat(p.body)); sh.position.set(0, 1.72, -0.35); m.silhouette.add(sh);
  const st = box(m.silhouette, 0.34, 0.34, 2.1, p.metal, 0, 2.6, 0.75); st.rotation.x = -0.9;
}

// ---------------------------------------------------------------------------
// The 6×6, as a BTR (67.60 step 2; the owner, DECISIONS 197: "make the current apc be more of a btr like design, but
// obviously with the manned .50 rather than a larger gun")
// ---------------------------------------------------------------------------

/**
 * The APC, D, toward a BTR (proportions from the owner's references, nothing copied): a boat hull — a sharp bow, its lower
 * plate raked back under to the front wheels, the upper hull's sides sloping in to a flat roof — on eight wheels outboard
 * in two pairs (the front four steering), a side hatch between the middle axles each side, the driver's front flaps on the
 * upper glacis, the engine deck's louvres; today's manned .50 cupola, the driver's lid, the bay's roof hatches and the rear
 * door (today's ramp: the bay's seats and exits do not move).
 */
function btrD(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m;
  const WAIST = 1.55, ROOF = 2.26, R = 0.52;
  for (const side of [-1, 1]) for (const z of [2.2, 1.12, -1.0, -2.08]) {
    const steer = group(full, side * 1.2, R, z);
    m.anim.wheels.push(wheelD(steer, R, 0.42, p, 0, 0, 0, 'truck'));
    if (z > 0) m.anim.steer.push(steer);
  }
  m.anim.wheelRadius = R;
  // the lower hull: the boat's bow (its lower plate raked back under), the belly, the rear; flat to the waist
  slab(hull, [3.36, 1.22, 2.5, 0.5, -2.95, 0.5, -3.3, 0.92, -3.3, WAIST, 2.95, WAIST], 2.3, p.body, 0, 0.06).name = 'body';
  // the upper hull: its sides sloping in from the waist to the roof (a front profile along the body), the upper glacis
  front(hull, [-1.15, WAIST, 1.15, WAIST, 0.93, ROOF, -0.93, ROOF], 5.05, p.body, -0.72, 0.05);
  slab(hull, [1.75, ROOF, 1.75, WAIST, 2.95, WAIST, 3.1, 1.44], 1.86, p.body, 0, 0.06);
  // the waist's ledge each side (the fender line over the wheels), the flash stripe on the sloped side, vision blocks in it
  for (const side of [-1, 1]) {
    slab(hull, rect(-3.0, WAIST - 0.06, 2.9, WAIST + 0.02), 0.1, p.dark, side * 1.17, 0.01);
    const slope = group(hull, side * 1.04, (WAIST + ROOF) / 2, 0); slope.rotation.z = side * 0.31;
    box(slope, 0.03, 0.2, 4.6, p.flash, side * 0.02, -0.08, -0.6);
    for (const z of [0.95, -0.45, -1.35, -2.25]) box(slope, 0.03, 0.12, 0.3, GLASS, side * 0.025, 0.18, z);
    for (const z of [2.2, 1.12, -1.0, -2.08]) profile(hull, arch(z, R, 0, R + 0.08).slice(0, 10), 0.14, p.dark, side * 1.105);   // the wells (2.5 cm proud of the hull's side: they sat on it and fought it)
    // the side hatch between the middle axles: hinged at its front edge, swinging out
    const sh = hinge(m, hull, side * 1.17, 0, 0.42, 'y', -side * 1.25, 'door');
    slab(sh, [-0.78, 0.72, 0.0, 0.72, 0.0, WAIST - 0.1, -0.78, WAIST - 0.1], 0.05, p.body, 0, 0.012, [rev(rect(-0.5, 1.2, -0.28, 1.34))]);
    box(sh, 0.03, 0.03, 0.12, p.metal, side * 0.03, 1.05, -0.62);
    loose(box(hull, 0.16, 0.12, 0.05, RED, side * 0.9, 1.9, -3.28));
  }
  // the bow: the driver's two flaps on the upper glacis, headlamps in guards, tow hooks; the trim plate
  const rake = Math.atan2(ROOF - 1.44, 3.1 - 1.75);
  for (const x of [-0.42, 0.42]) { const f = group(hull, x, (ROOF + 1.44) / 2 + 0.03, (1.75 + 3.1) / 2 - 0.05); f.rotation.x = rake; box(f, 0.7, 0.04, 0.55, p.metal, 0, 0, 0); box(f, 0.6, 0.02, 0.12, GLASS, 0, 0.02, 0.18); }
  for (const x of [-0.85, 0.85]) {
    cyl(hull, 0.1, 0.06, p.metal, x, 1.38, 3.02, 'z', 10); cyl(hull, 0.075, 0.02, LAMP, x, 1.38, 3.055, 'z', 10);
    box(hull, 0.1, 0.12, 0.12, p.metal, x * 0.5, 0.7, 2.75);
  }
  // the driver's hatch and lid (today's place), the bay's roof hatches (theirs), the engine deck's louvres
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, -0.55, ROOF + 0.03, 1.25);
  const lid = hinge(m, hull, -0.55, ROOF + 0.06, 0.95, 'x', -Math.PI, 'hatch', 'driver');
  front(lid, rect(-0.29, 0, 0.29, 0.04), 0.58, p.metal, 0.29, 0.01).name = 'lid';
  m.crew.driver = anchor(hull, -0.55, 2.29 - HATCH_DEPTH, 1.25);
  for (const x of [-0.45, 0.45]) {
    const h = hinge(m, hull, x, ROOF + 0.02, -2.15, 'x', -1.2, 'bayHatch');
    front(h, rect(-0.3, 0, 0.3, 0.035), 0.6, p.body, 0.3, 0.008);
  }
  const louvres: Pts[] = []; for (let i = 0; i < 5; i++) louvres.push(rev(rect(-0.7 + i * 0.3, -3.05, -0.5 + i * 0.3, -2.5)));
  box(hull, 1.5, 0.02, 0.6, RUBBER, 0, ROOF + 0.005, -2.78);
  top(hull, rect(-0.8, -3.1, 0.8, -2.45), 0.04, p.metal, ROOF + 0.01, 0.008, louvres);
  // the cupola and its .50 (today's mount and stand), the rear door (today's ramp: the bay's seats and exits)
  const turret = group(hull, 0.35, 2.27, -0.3); m.turret = turret;
  top(turret, ring(0, 0, 0.45, 8, Math.PI / 8), 0.3, p.dark, 0, 0.03);
  top(turret, ring(0, 0, 0.47, 8, Math.PI / 8), 0.04, p.metal, 0.28);
  m.crew.gunner = anchor(turret, 0, 0.3 - HATCH_DEPTH, 0);
  const cradle = group(turret, 0.0, 0.42, 0.3); m.cradle = cradle;
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);
  m.crew.gunner.position.y += HMG_CUPOLA_STAND;
  m.muzzle = hmg.muzzle;
  m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  const ramp = group(hull, 0, 0.6, -3.28); m.anim.ramp = ramp;
  front(ramp, rect(-0.7, 0, 0.7, 1.55), 0.09, p.body, 0, 0.02, [rev(rect(-0.2, 1.22, 0.2, 1.3))]);
  box(ramp, 0.4, 0.08, 0.02, GLASS, 0, 1.26, 0.02);
  box(ramp, 1.42, 0.08, 0.1, p.flash, 0, 1.5, 0);
  for (const y of [0.25, 0.55, 0.85, 1.15]) box(ramp, 1.24, 0.04, 0.03, p.metal, 0, y, 0.06);
  box(hull, 1.5, 1.6, 0.04, p.dark, 0, 1.4, -3.27);
  m.bay = anchor(ramp, 0, 1.6, 0);
  for (const x of [-1.35, -0.45, 0.45, 1.35]) m.bayExits.push(anchor(full, x, 0, -5.4 - Math.abs(x) * 0.3));
  const fit = group(full); m.anim.fittings = fit;   // (no outboard fittings: the ledge and wells ride the hull)
  m.sides = { x: 1.15, y: [0.95, 1.48], z: [-2.9, 2.3] };
  m.roofAt = () => ROOF;   // (the wreck's burn on it)   // (a damaged form's scorch on the flat lower hull: the upper sides slope in)
  // far tier: the boat hull and its wheels
  slab(m.silhouette, [3.36, 1.22, 2.5, 0.5, -2.95, 0.5, -3.3, 0.92, -3.3, ROOF, 1.75, ROOF, 3.1, 1.44], 2.3, p.body);
  for (const z of [1.66, -1.54]) box(m.silhouette, 2.84, 1.04, 2.1, p.dark, 0, 0.52, z);
  const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.46, 0.3, 8), mat(p.dark)); sc.position.set(0.35, 2.42, -0.3); m.silhouette.add(sc);
}

// ---------------------------------------------------------------------------
// The tracked carrier, as an M113 (the owner: "Then we'll probably want that m113 style model")
// ---------------------------------------------------------------------------

/**
 * The carrier, D, toward an M113 (proportions from the owner's references, nothing copied): a box hull over its tracks —
 * the side plates down over the track's top run, a steep upper glacis and the nose under it with its trim vane — on five
 * road wheels, the drive sprocket in front and the idler behind; on the roof the driver's hatch front left, the
 * commander's cupola with the manned .50, the big cargo hatch behind; the rear ramp (a bay: what lane C would add to the
 * carrier's seats). No field tube. Its hull stands taller than today's carrier (a box to 2.0 person units, not 1.48): the
 * hit shape's hull box grows (BUILDLIST 67.60: the numbers for lane C; the carrier is not a body in play yet).
 */
function m113D(m: VehicleModel, p: UnitPalette, opts: VehicleLookOptions = {}): void {
  const { full, hull } = m;
  const ROOF = 2.0, SIDE = 0.8, HALF = 1.48;
  for (const side of [-1, 1]) {
    const x = side * 1.22;
    m.anim.tracks.push(trackLoopD(full, x, -2.3, 2.2, 0.46, 0.46, 0.56, p));
    m.anim.wheels.push(wheelD(full, 0.42, 0.42, p, x, 0.5, 2.2, 'sprocket'));   // the drive sprocket, in front
    m.anim.wheels.push(wheelD(full, 0.38, 0.42, p, x, 0.46, -2.3, 'idler'));
    for (let i = 0; i < 5; i++) m.anim.wheels.push(wheelD(full, 0.33, 0.4, p, x, 0.37, -1.62 + i * 0.8, 'road'));
  }
  m.anim.wheelRadius = 0.36;
  // the hull: one side profile, full width over the tracks (its sides down to SIDE), the steep upper glacis, the nose
  slab(hull, [-2.62, SIDE, -2.62, ROOF, 1.75, ROOF, 2.62, 1.4, 2.72, 1.12, 2.55, SIDE], 2 * HALF, p.body, 0, 0.08).name = 'body';
  box(hull, 1.9, SIDE - 0.42, 4.9, p.dark, 0, (SIDE + 0.42) / 2, 0);                                       // the belly between the tracks
  for (const side of [-1, 1]) {
    slab(hull, rect(-2.5, SIDE + 0.02, 2.45, SIDE + 0.1), 0.05, p.dark, side * (HALF + 0.02), 0.01);       // the side's lower lip
    slab(hull, rect(-2.0, 1.55, 1.2, 1.68), 0.03, p.flash, side * (HALF + 0.015));                         // the team's band
    loose(box(hull, 0.16, 0.1, 0.03, RED, side * 1.1, 1.75, -2.64));
    cyl(hull, 0.1, 0.06, p.metal, side * 1.05, 1.5, 2.47, 'z', 10).rotation.x = Math.PI / 2 - Math.atan2(ROOF - 1.4, 2.62 - 1.75);
  }
  // the trim vane folded on the glacis (a plate on its hinge line), the exhaust's grille on the right front, tow hooks
  const vaneRake = Math.atan2(ROOF - 1.4, 2.62 - 1.75);
  const vane = group(hull, 0, (ROOF + 1.4) / 2 + 0.04, (1.75 + 2.62) / 2); vane.rotation.x = vaneRake;
  box(vane, 2.3, 0.05, 0.8, p.body, 0, 0, 0);
  const grille: Pts[] = []; for (let i = 0; i < 4; i++) grille.push(rev(rect(0.3 + i * 0.16, 1.25, 0.4 + i * 0.16, 1.7)));
  slab(hull, rect(0.2, 1.2, 1.0, 1.75), 0.03, p.metal, -(HALF + 0.02), 0.008, grille);
  for (const x of [-0.8, 0.8]) box(hull, 0.12, 0.14, 0.12, p.metal, x, 0.95, 2.7);
  // the driver's hatch front left, its lid swinging over on its inboard hinge
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, 0.6, ROOF + 0.03, 1.3);
  const dl = hinge(m, hull, 0.3, ROOF + 0.06, 1.3, 'z', Math.PI, 'hatch', 'driver');
  top(dl, rect(0, -0.29, 0.58, 0.29), 0.05, p.metal, 0, 0.012).name = 'lid';
  m.crew.driver = anchor(hull, 0.6, ROOF + 0.03 - HATCH_DEPTH, 1.3);
  // the cargo hatch behind: one big lid on its rear hinge (the troops stand in it: the spotter's seat)
  box(hull, 1.5, 0.05, 1.3, p.dark, 0, ROOF + 0.02, -1.6);
  if (opts.armament !== 'mortar') {   // (69.23: the mortar's rig brings its own hatch, in three leaves)
    const ch = hinge(m, hull, 0, ROOF + 0.05, -2.28, 'x', -(Math.PI / 2 + 0.3), 'hatch', 'spotter');   // (open: leaning back over the rear, as the references rest it)
    top(ch, rect(-0.78, 0, 0.78, 1.36), 0.05, p.body, 0, 0.012);
  }
  m.crew.spotter = anchor(hull, 0.4, ROOF - HATCH_DEPTH, -1.6);
  // 69.23: the mortar carrier — the owner's picks on lane E's sheet (DECISIONS 241; "Let's take the lean for mortar design (tube
  // a, turntable, hatch b, crew of 1)"): the medium tube on its turntable on a pedestal under the cargo hatch, the round hatch
  // in three leaves (in the lid's place), the store in racks along the walls, the ready rack at the rim; posed in play from the
  // carrier's state (rendering/mortarLooks.ts poseMortarCrew)
  if (opts.armament === 'mortar') {
    m.mortar = buildMortarRig(hull, p, MORTAR_PICKS);
    poseMortar(m.mortar, { set: 0, rounds: 0, ready: 0 });
  }
  // the commander's cupola: a ring with its vision blocks, the gunner standing in it, the .50 on its pintle (it turns)
  const turret = group(hull, -0.35, ROOF, 0.35); m.turret = turret;
  top(turret, ring(0, 0, 0.46, 8, Math.PI / 8), 0.26, p.dark, 0, 0.03);
  for (let k = 0; k < 6; k++) { const a = -1.3 + k * 0.52; box(turret, 0.13, 0.08, 0.05, GLASS, Math.sin(a) * 0.46, 0.18, Math.cos(a) * 0.46).rotation.y = a; }
  m.crew.gunner = anchor(turret, 0, 0.26 - HATCH_DEPTH, 0);
  const post = cyl(turret, 0.045, 0.4, p.metal, 0, 0.45, 0.42, 'y', 6); post.userData.thin = true;
  const cradle = group(turret, 0, 0.66, 0.3); m.cradle = cradle;
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);
  m.muzzle = hmg.muzzle;
  m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  const aerial = loose(box(turret, 0.025, 1.3, 0.025, p.metal, -0.4, 0.75, -0.3)); aerial.rotation.x = -0.1; aerial.userData.thin = true;
  // the rear ramp, hinged at its foot: the full rear plate, its door in it, the ribs inside
  const ramp = group(hull, 0, SIDE - 0.1, -2.66); m.anim.ramp = ramp;
  front(ramp, rect(-1.0, 0, 1.0, ROOF - SIDE + 0.08), 0.09, p.body, 0, 0.02);
  slab(ramp, rect(-0.04, 0.2, 0.04, 1.0), 0.6, p.dark, 0);
  for (const y of [0.25, 0.55, 0.85]) box(ramp, 1.8, 0.04, 0.03, p.metal, 0, y, 0.06);
  box(hull, 2.1, ROOF - SIDE, 0.04, p.dark, 0, (ROOF + SIDE) / 2, -2.62);
  m.bay = anchor(ramp, 0, ROOF - SIDE + 0.1, 0);
  for (const x of [-1.35, -0.45, 0.45, 1.35]) m.bayExits.push(anchor(full, x, 0, -4.7 - Math.abs(x) * 0.3));
  // far tier: the box and its tracks
  box(m.silhouette, 3.0, 0.95, 5.2, p.dark, 0, 0.5, -0.05);
  slab(m.silhouette, [-2.62, SIDE, -2.62, ROOF, 1.75, ROOF, 2.62, 1.4, 2.72, 1.12, 2.55, SIDE], 2 * HALF, p.body);
  const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.3, 8), mat(p.dark)); sc.position.set(-0.35, ROOF + 0.15, 0.35); m.silhouette.add(sc);
}

// ---------------------------------------------------------------------------

/** 67.77: the builds' options — `smoke` bolts on the kind's smoke launchers (SMOKE_LAUNCHER_FITS; in play with GameConfig.vehicleSmoke). */
export interface VehicleLookOptions { smoke?: boolean; /** 69.21: what a technical's mount carries (absent: the .50); 69.23: 'mortar' on the M113, its tube in the cargo hatch */ armament?: MountArmament; }

/** Build a vehicle in the D look (`D_VEHICLES`), as `buildVehicleModel` builds today's: same interface, anchors and envelope. */
export function buildVehicleLook(kind: DVehicle, p: UnitPalette, personHeight: number, variant: BikeVariant = 'gas', opts: VehicleLookOptions = {}): VehicleModel {
  const m = vehicleShell(kind as VehicleKind, personHeight);
  if (kind === 'motorcycle') motorcycleD(m, p, variant);
  else if (kind === 'technical') technicalD(m, p, opts.armament);
  else if (kind === 'supplyTruck') supplyTruckD(m, p);
  else if (kind === 'wheeled') wheeledD(m, p);
  else trackedD(m, p);
  if (opts.smoke) fitSmokeLaunchers(m, SMOKE_LAUNCHER_FITS[kind], p);
  m.silhouette.visible = false;
  updateVehicleModel(m, { speed: 0, aimPitch: kind === 'tracked' ? 0.9 : 0.15 }, 0);
  return m;
}

/** A model's triangles as drawn at its full tier (the far tier left out). */
export function vehicleTriangles(m: VehicleModel): number {
  let n = 0;
  m.full.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible) return;
    for (let q: THREE.Object3D | null = o; q && q !== m.full; q = q.parent) if (!q.visible) return;
    const g = mesh.geometry, tris = g.index ? g.index.count / 3 : g.attributes.position.count / 3;
    n += tris * ((mesh as THREE.InstancedMesh).isInstancedMesh ? (mesh as THREE.InstancedMesh).count : 1);
  });
  return n;
}
/** A model's draws at its full tier (each mesh once; an instanced mesh is one): after `bakeVehicleModel`, what play draws. */
export function vehicleDraws(m: VehicleModel): number {
  let n = 0;
  m.full.traverse(o => {
    if (!(o as THREE.Mesh).isMesh || !o.visible) return;
    for (let q: THREE.Object3D | null = o; q && q !== m.full; q = q.parent) if (!q.visible) return;
    n++;
  });
  return n;
}

/** 67.60 step 2 (the owner, DECISIONS 197), proposed on the sheet: the APC toward a BTR, the carrier toward an M113. Not in play until he picks. */
export type VehicleShape = 'btr' | 'm113';
export function buildVehicleShape(shape: VehicleShape, p: UnitPalette, personHeight: number, opts: VehicleLookOptions = {}): VehicleModel {
  const kind = shape === 'btr' ? 'wheeled' : 'tracked';
  const m = vehicleShell(kind, personHeight);
  if (shape === 'btr') btrD(m, p); else m113D(m, p, opts);
  if (opts.smoke) fitSmokeLaunchers(m, SMOKE_LAUNCHER_FITS[kind], p);
  m.silhouette.visible = false;
  updateVehicleModel(m, { speed: 0, aimPitch: 0.15 }, 0);
  return m;
}
